import React, { useEffect, useState } from 'react';
import { Box, Typography, Fade, Chip } from '@mui/material';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';

interface MarketplacePurchaseNotification {
  buyerUsername: string;
  nftName: string;
  nftType: string;
  nftRarity: string;
  priceInINC: string;
  timestamp: Date;
}

const MarketplaceNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<MarketplacePurchaseNotification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    // Connect to the main server socket for notifications
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:4242', {
      withCredentials: true,
      transports: ['websocket', 'polling']
    });

    setSocket(newSocket);

    // Listen for marketplace purchase notifications
    newSocket.on('marketplace-purchase', (data: MarketplacePurchaseNotification) => {
      setNotifications(prev => [data, ...prev]);
      
      // Auto-remove notification after 5 seconds
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.timestamp !== data.timestamp));
      }, 5000);
    });

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);

  const getRarityColor = (rarity: string) => {
    switch (rarity.toLowerCase()) {
      case 'common':
        return '#9e9e9e';
      case 'uncommon':
        return '#4caf50';
      case 'rare':
        return '#2196f3';
      case 'epic':
        return '#9c27b0';
      case 'legendary':
        return '#ff9800';
      default:
        return '#757575';
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 20,
        right: 20,
        zIndex: 9999,
        width: 350,
        maxWidth: '90vw',
        pointerEvents: 'none'
      }}
    >
      {notifications.map((notification, index) => (
        <Fade in key={`${notification.timestamp}-${index}`} timeout={500}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              borderRadius: 2,
              border: '1px solid #38e8ff',
              boxShadow: '0 4px 20px rgba(56,232,255, 0.3)',
              pointerEvents: 'auto'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ShoppingCartIcon sx={{ color: '#38e8ff', mr: 1 }} />
              <Typography variant="body1" fontWeight="bold" color="white">
                Marketplace Purchase!
              </Typography>
            </Box>
            
            <Typography variant="body2" color="white" sx={{ mb: 1 }}>
              <span style={{ color: '#38e8ff', fontWeight: 'bold' }}>
                {notification.buyerUsername}
              </span>{' '}
              purchased{' '}
              <span style={{ fontWeight: 'bold' }}>
                {notification.nftName}
              </span>
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Chip
                label={notification.nftRarity}
                size="small"
                sx={{
                  backgroundColor: getRarityColor(notification.nftRarity),
                  color: 'white',
                  fontWeight: 'bold'
                }}
              />
              <Chip
                label={notification.nftType}
                size="small"
                variant="outlined"
                sx={{
                  borderColor: '#38e8ff',
                  color: '#38e8ff'
                }}
              />
            </Box>
            
            <Typography variant="body2" color="#38e8ff" fontWeight="bold">
              {notification.priceInINC} INC
            </Typography>
          </Box>
        </Fade>
      ))}
    </Box>
  );
};

export default MarketplaceNotifications; 